import React from 'react';
import {Box} from '@mui/material';
import {CategoryDetails, Category} from "@/types";
import {useResizeHook} from "@/lib";
import {useTheme} from '@mui/material/styles';
import CategoryHighlightsContent from "@/ui/category-highlights/category-highlights-content";
import CategoryHighlightsGallery from "@/ui/category-highlights/category-highlights-gallery";

const CategoryHighlights: ({
                               category: {name, description, images, subCategories, points},
                               alternate,
                               alternateBackground,
                               hideServicesLinks,
                               showContactsButton,
                               showPoints,
                               hideSubtitle
                           }: {
    category: CategoryDetails & Category;
    alternate?: boolean;
    alternateBackground?: boolean;
    hideServicesLinks?: boolean;
    showContactsButton?: boolean;
    showMainCategoryLink?: boolean;
    showPoints?: boolean;
    hideSubtitle?: boolean
}) => React.JSX.Element = ({
                               category,
                               alternate = false,
                               alternateBackground = false,
                               hideServicesLinks = false,
                               showContactsButton = false,
                               showPoints = false,
                               hideSubtitle = false,
                               showMainCategoryLink = false,
                           }) => {


    const theme = useTheme();
    const localTheme = alternateBackground ? theme.custom.brandedAlternateBackgroundBlock : theme.custom.brandedBackgroundBlock;
    const {lessThanTablet} = useResizeHook();

    const patternDots = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg id='hexagons' fill='%23ffffff' fill-opacity='0.4' fill-rule='nonzero'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"

    const patternDiagonal = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg id='hexagons' fill='%23ffffff' fill-opacity='0.4' fill-rule='nonzero'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"

    const backgroundImage = (pattern: string) => {
        return {backgroundImage: `url("${pattern}")`}
    }

    const pattern = alternateBackground ? patternDots : patternDiagonal

    const containerStyles = {
        // backgroundImage: `linear-gradient(${localTheme.direction}, ${localTheme.start}, ${localTheme.end})`,
        ...backgroundImage(pattern),
        backgroundColor: '#ffffff',
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover',
        '& h1, & h2, & h3, & h4, & h5, & h6, & a': {
            color: localTheme.color,
        },
        '& a': {
            borderColor: localTheme.color,
        },
        '& >div': {
            // backgroundColor: localTheme.background,
        },
        [theme.breakpoints.down('md')]: {
            pb: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',

        }
    }

    const gridStyles = {
        justifyContent: "space-around",
        alignItems: "center",
        py: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',

        [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: '1fr',
        },
        [theme.breakpoints.down('md')]: {
            mb: 5,
            // borderRadius: '10px',
            width: 'calc(100% - 10px)',
            margin: 'auto',
            py: 0,
            // mt: 5,
        },
    }

    /** RENDER BLOCKS **/
    const gallery = <CategoryHighlightsGallery category={category}/>

    const content = <CategoryHighlightsContent
        category={category}
        hideServicesLinks={hideServicesLinks}
        showContactsButton={showContactsButton}
        showPoints={showPoints}
        hideSubtitle={hideSubtitle}
        alternateBackground={alternateBackground}
        showMainCategoryLink={showMainCategoryLink}
    />
    /** END RENDER BLOCKS **/


    /** CONTAINER **/
    return (
        <Box sx={containerStyles}
             className='fade-in'>
            <Box sx={gridStyles}>
                <Box>
                    {lessThanTablet ? content : alternate ? content : gallery}
                </Box>
                <Box
                    sx={(theme) => ({
                        [theme.breakpoints.down('lg')]: {
                            display: 'none', //hide completely for smaller viewports
                        }
                    })}
                >
                    {alternate ? gallery : content}
                </Box>
            </Box>
        </Box>
    );
};

export default CategoryHighlights;
